import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useContractorAuth } from "../context/ContractorAuthContext";
import { ContractorShell } from "../components/ContractorShell";
import { permitMeta } from "../lib/permitSchemas";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Loader2, UserCog, ShieldCheck, FileCheck2, QrCode, MapPin, ClipboardCheck, Download } from "lucide-react";

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return dt; }
}

export default function ContractorPortal() {
  const { data, setData, refresh } = useContractorAuth();
  const navigate = useNavigate();
  const profile = data?.profile || {};
  const inductions = data?.inductions || [];
  const permits = data?.permits || [];
  const submissions = data?.submissions || [];
  const pendingSubs = submissions.filter((s) => s.status === "pending");

  const [editOpen, setEditOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [site, setSite] = useState("");
  const [sites, setSites] = useState([]);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);

  const openEdit = () => {
    setEdit({
      first_name: profile.first_name || "", last_name: profile.last_name || "",
      company: profile.company || "", mobile: profile.mobile || "",
      dbs_number: profile.dbs_number || "", emergency_contact: profile.emergency_contact || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/contractor/me", edit);
      setData(d);
      toast.success("Details updated");
      setEditOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const openStart = async () => {
    setStartOpen(true);
    try { const r = await api.get("/contractor/sites"); setSites(r.data.sites); } catch (_) {}
  };

  return (
    <ContractorShell>
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">Contractor portal</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground" data-testid="portal-welcome">
          Hi {profile.first_name || "there"}
        </h1>
        <p className="text-sm text-muted-foreground">Complete your site inductions and track your permits here.</p>
      </div>

      {/* Start induction */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 rounded-xl border border-brand/30 bg-brand/5 p-5 sm:flex-row sm:items-center" data-testid="start-induction-card">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-brand/10 p-2 text-brand"><ClipboardCheck className="h-5 w-5" /></div>
          <div>
            <div className="font-heading text-lg font-bold tracking-tight text-foreground">Complete a site induction</div>
            <p className="text-sm text-muted-foreground">Scan the QR code displayed at a site, or choose a site below to begin.</p>
          </div>
        </div>
        <Button className="bg-brand hover:bg-brand-dark" onClick={openStart} data-testid="start-induction-btn">
          <QrCode className="mr-2 h-4 w-4" /> Start induction
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile */}
        <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 font-heading font-bold tracking-tight"><UserCog className="h-4 w-4 text-brand" /> Your details</div>
            <Button variant="outline" size="sm" onClick={openEdit} data-testid="edit-profile-btn">Edit</Button>
          </div>
          <dl className="space-y-2 text-sm">
            <Row label="Reference" value={profile.reference} testid="p-reference" />
            <Row label="Name" value={profile.name} testid="p-name" />
            <Row label="Company" value={profile.company} testid="p-company" />
            <Row label="Email" value={profile.email} testid="p-email" />
            <Row label="Telephone" value={profile.mobile} testid="p-mobile" />
            <Row label="DBS number" value={profile.dbs_number} testid="p-dbs" />
          </dl>
        </div>

        {/* Inductions */}
        <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 font-heading font-bold tracking-tight"><ShieldCheck className="h-4 w-4 text-brand" /> Site inductions</div>
          {pendingSubs.length > 0 && (
            <div className="mb-3 space-y-1" data-testid="pending-submissions">
              {pendingSubs.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-md bg-amber-50 px-3 py-2 text-xs">
                  <span className="flex items-center gap-1 text-amber-800"><MapPin className="h-3 w-3" /> {s.site}</span>
                  <Badge className="bg-amber-500 text-white">Awaiting review</Badge>
                </div>
              ))}
            </div>
          )}
          {inductions.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="no-inductions">No approved inductions yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="inductions-list">
              {inductions.map((i) => (
                <li key={i.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-brand" /> {i.site}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Exp {fmt(i.induction_expiry)}</span>
                    {i.active ? <Badge className="bg-green-600 text-white">Valid</Badge> : <Badge className="bg-red-600 text-white">Expired</Badge>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Permits */}
        <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 font-heading font-bold tracking-tight"><FileCheck2 className="h-4 w-4 text-brand" /> Active permits</div>
          {permits.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="no-permits">No permits issued yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="permits-list">
              {permits.map((p) => (
                <li key={p.permit_number} className="rounded-md border border-border px-3 py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-brand">{p.permit_number}</span>
                    {p.active ? <Badge className="bg-green-600 text-white">Active</Badge> : <Badge variant="outline">Expired</Badge>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(p.permit_types || []).map((t) => (
                      <span key={t} className="rounded px-1.5 py-0.5 text-[10px] font-semibold text-white" style={{ background: permitMeta(t)?.color }}>{permitMeta(t)?.short}</span>
                    ))}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{p.site_location} · exp {fmt(p.expiry_at)}</div>
                  <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => navigate(`/contractor/permits/${p.id}`)} data-testid={`view-permit-${p.permit_number}`}>
                    <Download className="mr-1.5 h-3.5 w-3.5" /> View / Download PDF
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="edit-profile-dialog">
          <DialogHeader><DialogTitle>Edit your details</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <EField label="First name" testid="ep-first" value={edit.first_name} onChange={(v) => setEdit((f) => ({ ...f, first_name: v }))} />
            <EField label="Surname" testid="ep-last" value={edit.last_name} onChange={(v) => setEdit((f) => ({ ...f, last_name: v }))} />
            <div className="col-span-2"><EField label="Company" testid="ep-company" value={edit.company} onChange={(v) => setEdit((f) => ({ ...f, company: v }))} /></div>
            <EField label="Telephone" testid="ep-mobile" value={edit.mobile} onChange={(v) => setEdit((f) => ({ ...f, mobile: v }))} />
            <EField label="DBS number" testid="ep-dbs" value={edit.dbs_number} onChange={(v) => setEdit((f) => ({ ...f, dbs_number: v }))} />
            <div className="col-span-2"><EField label="Emergency contact" testid="ep-emergency" value={edit.emergency_contact} onChange={(v) => setEdit((f) => ({ ...f, emergency_contact: v }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-dark" onClick={saveEdit} disabled={busy} data-testid="save-profile-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start induction dialog */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent className="max-w-md" data-testid="start-induction-dialog">
          <DialogHeader><DialogTitle>Choose your site</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Select the site you are working at to start its induction.</p>
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger data-testid="start-site-select"><SelectValue placeholder="Select a site" /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button className="bg-brand hover:bg-brand-dark" disabled={!site} onClick={() => navigate(`/self-induct/${encodeURIComponent(site)}`)} data-testid="start-go-btn">
              Begin induction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ContractorShell>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-right" data-testid={testid}>{value || "—"}</dd>
    </div>
  );
}
function EField({ label, testid, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
